import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { VehiclePartCategoryTreeItem } from '@1po/1po-bff-fe-spec/generated/catalog/category/model/VehiclePartCategoryTreeItem';
import { ROUTER_CATALOG_DH, ROUTER_CATALOG_IAM, ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import {
  getExplodedIAMTree,
  getExplodedIAMTreeItem,
  getExplodedOEMTree,
  getExplodedTree,
  getExplodedTreeItem,
} from 'domains/catalog/Catalog.store';
import { OEM_BRAND_KEY } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl, isDH, isIAM, useCatalogSourceLocation } from 'domains/catalog/Catalog.utils';
import { getIamCatalogBrandsView } from 'domains/user';
import { Breadcrumb } from 'UI';

const CatalogBreadcrumb = () => {
  const { t } = useTranslation();
  const catalogSource = useCatalogSourceLocation();
  const catalogPath = isDH(catalogSource) ? ROUTER_CATALOG_DH : ROUTER_CATALOG_IAM;
  const match = useRouteMatch<{
    query: string;
    carPartGroupId?: string;
    categoryId?: string;
    subcategoryId?: string;
  }>(`${catalogPath}/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId?/:categoryId?/:subcategoryId?`);

  const { query = '', carPartGroupId = '', categoryId = '', subcategoryId = '' } = match?.params || {};
  const { search } = useLocation();
  const vehicleKey = query;
  const iamCatalogView = useSelector(getIamCatalogBrandsView);
  const isOEM = iamCatalogView === OEM_BRAND_KEY;
  const catalogSourceUrl = getCatalogSourceUrl(catalogSource);
  const urlParams = new URLSearchParams(search);

  const tree = useSelector((state: RootState) => {
    if (isIAM(catalogSource) && isOEM) {
      return getExplodedOEMTree(state, vehicleKey);
    }
    if (isIAM(catalogSource)) {
      return getExplodedIAMTree(state, vehicleKey);
    }
    return getExplodedTree(state, vehicleKey);
  });
  const treeData = tree?.data;
  const treeStatus = tree?.searchStatus;

  const getItem = (arr: string[]) => {
    if (isDH(catalogSource)) {
      return getExplodedTreeItem(treeData as VehiclePartCategoryTreeItem[], arr);
    } else if (isIAM(catalogSource)) {
      return getExplodedIAMTreeItem(treeData, arr);
    } else {
      return undefined;
    }
  };

  const carPartGroup = carPartGroupId && getItem([carPartGroupId]);
  const category = categoryId && getItem([carPartGroupId, categoryId]);
  const subcategory = subcategoryId && getItem([carPartGroupId, categoryId, subcategoryId]);

  if (carPartGroupId && !carPartGroup) {
    return null;
  }

  const getBreadcrumbItems = () => {
    const breadcrumbItems = [
      {
        link: '/',
        label: t('common.home', 'Home'),
      },
      {
        link: `${catalogSourceUrl}/${query}${ROUTER_CATALOG_VEHICLE}?${urlParams}`,
        label: t('common.my_vehicle', 'My vehicle'),
      },
    ];

    const basePath = `${catalogSourceUrl}/${query}${ROUTER_CATALOG_VEHICLE}/${carPartGroupId}`;

    carPartGroupId &&
      carPartGroup &&
      breadcrumbItems.push({
        link: `${basePath}?${urlParams}`,
        label: carPartGroup?.label ?? '',
      });
    categoryId &&
      category &&
      breadcrumbItems.push({
        link: `${basePath}/${categoryId}?${urlParams}`,
        label: category?.label ?? '',
      });
    subcategoryId &&
      subcategory &&
      breadcrumbItems.push({
        link: `${basePath}/${categoryId}/${subcategoryId}?${urlParams}`,
        label: subcategory?.label ?? '',
      });
    return breadcrumbItems;
  };

  return (
    <DataContainer data={treeStatus} ErrorState={() => <></>} NotFound={() => <></>}>
      <Breadcrumb items={getBreadcrumbItems()} />
    </DataContainer>
  );
};

export default CatalogBreadcrumb;
